import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type Announcements,
  type DragEndEvent,
  type DragStartEvent,
  type ScreenReaderInstructions,
  closestCenter,
  DndContext,
  DragOverlay,
} from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { useLingui } from '@lingui/react/macro';

import type { ActionMenuItem } from '@rover/components/src/callouts/ActionMenu';
import { Box, Button, Flex } from '@rover/kibble/core';
import LoadingShimmer from '@rover/kibble/patterns/LoadingShimmer';
import MediaQuery from '@rover/kibble/styles/MediaQuery';
import useOnIntersect from '@rover/react-lib/src/hooks/useOnIntersect';

import AddImagesTile from './AddImagesTile';
import {
  ACTIONS_SELECTOR,
  GALLERY_CONTAINER_WIDTH,
  GALLERY_GRID_COLUMNS,
  GALLERY_GRID_GAP,
  GALLERY_MIN_TILE_SIZE,
} from './constants';
import type { DropTarget } from './dropToSlotAnimation';
import dropToSlotAnimation from './dropToSlotAnimation';
import GalleryPrivateBadge from './GalleryPrivateBadge';
import GalleryTile from './GalleryTile';
import type { GalleryItem, PaginationConfig } from './ImageGallery.common';
import {
  countGalleryVideoItems,
  galleryItemKey,
  isDimmedImage,
  isEditableGalleryImage,
  isLockedImage,
} from './ImageGallery.common';
import SortableImageTile from './SortableImageTile';
import useGridReorder from './useGridReorder';
import { getShimmerCount, shouldLoadMore, showsLoadMoreButton } from './utils';

type ImageGridContainerProps = {
  images: GalleryItem[];
  onImageClick: (index: number) => void;
  // Mobile only props
  // eslint-disable-next-line react/no-unused-prop-types
  onClose?: () => void;
  // eslint-disable-next-line react/no-unused-prop-types
  virtualizedGrid?: boolean;
  pagination?: PaginationConfig;
  renderImageOverlay?: (index: number) => ReactNode;
  onImagesAdded?: (files: File[]) => void;
  /** Reports a file the picker refused, which never reaches `onImagesAdded`. */
  onImagesAddedError?: (message: string) => void;
  isUploading?: boolean;
  renderImageActions?: (index: number) => ActionMenuItem[];
  columns?: number;
  onReorder?: (fromIndex: number, toIndex: number) => void;
  isReorderable?: (index: number) => boolean;
  /** Holds the grid still while a reorder is saving, rather than dropping the next one. */
  isReordering?: boolean;
  dimmedImageIds?: Set<number>;
  lockedImageIds?: Set<number>;
  isManaging?: boolean;
  selectedIds?: Set<number>;
  onToggleSelected?: (imageId: number) => void;
};

const ImageGridContainer = ({
  images,
  onImageClick,
  pagination,
  renderImageOverlay,
  onImagesAdded,
  onImagesAddedError,
  isUploading,
  renderImageActions,
  columns,
  onReorder,
  isReorderable,
  isReordering,
  dimmedImageIds,
  lockedImageIds,
  isManaging = false,
  selectedIds,
  onToggleSelected,
}: ImageGridContainerProps): JSX.Element | null => {
  const { t } = useLingui();
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const gridRef = useRef<HTMLElement | null>(null);
  // dnd-kit re-measures its rects after the drop, so keep the coordinates rather than the rect.
  const dropTarget = useRef<DropTarget | null>(null);
  const dropAnimation = useMemo(() => dropToSlotAnimation(() => dropTarget.current), []);
  const loadMoreButtonRef = useRef<HTMLElement | null>(null);
  const restoresFocusToGrid = useRef(false);

  const handleLoadMore = useCallback(() => {
    if (shouldLoadMore(pagination)) {
      pagination?.onLoadMore();
    }
  }, [pagination]);

  const sentinelRef = useOnIntersect(handleLoadMore, false);
  const hasLoadMoreButton = showsLoadMoreButton(pagination);

  // The last page takes the button away with it, so a keyboard or screen-reader
  // user who pressed it would be dropped onto the document body. Only a press
  // made from the button itself has focus to hand back.
  const handleLoadMorePress = useCallback(() => {
    restoresFocusToGrid.current = loadMoreButtonRef.current === document.activeElement;
    handleLoadMore();
  }, [handleLoadMore]);

  useEffect(() => {
    if (hasLoadMoreButton || !restoresFocusToGrid.current) return;

    restoresFocusToGrid.current = false;
    gridRef.current?.focus();
  }, [hasLoadMoreButton]);

  // `AddImagesTile` takes the first slot, pushing the last photo of the visible
  // first row onto the second one. This only decides fetch priority, so the
  // imprecision of the responsive `auto-fill` default costs nothing.
  const firstRowCount = (columns ?? GALLERY_GRID_COLUMNS) - (onImagesAdded ? 1 : 0);

  const { sensors, sortableKeys, isSortable, indexOfKey, willReorder, onDragEnd } = useGridReorder({
    images,
    onReorder,
    isReorderable,
    gridRef,
    isReordering,
  });

  // dnd-kit's default announcer reads the sortable id, which here is the photo's URL. These
  // announce the human-readable position instead.
  const accessibility = useMemo<{
    announcements: Announcements;
    screenReaderInstructions: ScreenReaderInstructions;
  }>(() => {
    const positionOf = (key: unknown): number => indexOfKey(key) + 1;
    const total = images.length;

    return {
      announcements: {
        onDragStart: ({ active }) => {
          const position = positionOf(active.id);

          return t`Picked up photo ${position}. Use the arrow keys to move it, then press space to drop it.`;
        },
        onDragOver: ({ active, over }) => {
          if (!over) {
            return undefined;
          }

          const position = positionOf(active.id);
          const target = positionOf(over.id);

          return t`Photo ${position} moved to position ${target} of ${total}.`;
        },
        onDragEnd: (event) => {
          const { active, over } = event;
          const position = positionOf(active.id);

          // Released outside the grid, onto a tile that cannot take it, or
          // while the last move is still saving: the photo has not moved, and
          // saying so is what tells a screen reader the drop was refused.
          if (!over || !willReorder(event)) {
            return t`Photo ${position} returned to position ${position} of ${total}.`;
          }

          const target = positionOf(over.id);

          return t`Photo ${position} dropped at position ${target} of ${total}.`;
        },
        onDragCancel: ({ active }) => {
          const position = positionOf(active.id);

          return t`Reordering cancelled. Photo ${position} returned to position ${position} of ${total}.`;
        },
      },
      screenReaderInstructions: {
        draggable: t`Press space to pick up a photo, the arrow keys to move it, space again to drop it, and escape to cancel.`,
      },
    };
  }, [t, indexOfKey, willReorder, images.length]);

  const handleDragStart = useCallback(({ active }: DragStartEvent) => {
    setDraggingKey(String(active.id));
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      // A refused drop has no slot to land in, so the tile animates back to
      // where it came from rather than to the one it was hovering.
      const over = willReorder(event) ? event.over?.rect : undefined;
      dropTarget.current = over ? { left: over.left, top: over.top } : null;
      setDraggingKey(null);
      onDragEnd(event);
    },
    [onDragEnd, willReorder]
  );

  const handleDragCancel = useCallback(() => {
    dropTarget.current = null;
    setDraggingKey(null);
  }, []);

  const draggingIndex = images.findIndex((image) => galleryItemKey(image) === draggingKey);
  const draggingImage = draggingIndex >= 0 ? images[draggingIndex] : undefined;

  const loadedImageCount = images.length - countGalleryVideoItems(images);
  const shimmerCount = getShimmerCount(pagination, loadedImageCount);

  const actionsRevealSx = (index: number): Record<string, unknown> => ({
    '@media (hover: hover)': {
      [`& ${ACTIONS_SELECTOR}`]: { opacity: openMenuIndex === index ? 1 : 0 },
      [`&:hover ${ACTIONS_SELECTOR}, & ${ACTIONS_SELECTOR}:focus-within`]: {
        opacity: 1,
      },
    },
  });

  const grid = (
    <>
      <Box
        ref={gridRef}
        data-testid="gallery-grid"
        webProps={{ tabIndex: -1 }}
        aria-busy={!!pagination?.isLoadingMore}
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(${GALLERY_MIN_TILE_SIZE}, 1fr))`,
          ...(columns
            ? {
                [MediaQuery.SM_UP.toString()]: {
                  gridTemplateColumns: `repeat(${columns}, 1fr)`,
                },
              }
            : {}),
          gap: GALLERY_GRID_GAP,
          margin: '0 auto',
          justifyContent: 'center',
          width: '100%',
          maxWidth: GALLERY_CONTAINER_WIDTH,
        }}
      >
        {onImagesAdded && (
          <AddImagesTile
            onImagesAdded={onImagesAdded}
            onSelectionError={onImagesAddedError}
            isUploading={isUploading}
          />
        )}
        {images.map((image, index) => {
          const key = galleryItemKey(image);
          const isLocked = isLockedImage(image, lockedImageIds);
          const isDimmed = isLocked || isDimmedImage(image, dimmedImageIds);
          const isEditable = isEditableGalleryImage(image);
          const imageId = image.id ?? undefined;
          const isSelectable = isManaging && isEditable && imageId !== undefined && !isLocked;
          const privateBadge = isLocked ? (
            <GalleryPrivateBadge isLocked />
          ) : (
            (isEditable && isDimmed && <GalleryPrivateBadge />) || null
          );
          const tile = (
            <GalleryTile
              image={image}
              index={index}
              columns={firstRowCount}
              onImageClick={onImageClick}
              overlay={renderImageOverlay?.(index) ?? privateBadge}
              actions={isLocked ? undefined : renderImageActions?.(index)}
              onActionsOpenChange={(isOpen) => setOpenMenuIndex(isOpen ? index : null)}
              isDimmed={isDimmed}
              isManaging={isManaging}
              isSelectable={isSelectable}
              isSelected={isSelectable && !!selectedIds?.has(imageId)}
              onToggleSelected={
                isSelectable && imageId !== undefined
                  ? () => onToggleSelected?.(imageId)
                  : undefined
              }
            />
          );

          return isSortable(index) ? (
            <SortableImageTile
              key={key}
              id={key}
              position={index + 1}
              isDisabled={isReordering}
              sx={actionsRevealSx(index)}
            >
              {tile}
            </SortableImageTile>
          ) : (
            <Box key={key} position="relative" width="100%" sx={actionsRevealSx(index)}>
              {tile}
            </Box>
          );
        })}
        {pagination?.isLoadingMore &&
          Array.from({ length: shimmerCount }, (_, i) => (
            <Box key={`shimmer-${i}`} width="100%" sx={{ aspectRatio: '1' }}>
              <LoadingShimmer
                width="100%"
                height="100%"
                borderRadius="primary"
                m="0x"
                data-testid="loading-more-shimmer"
              />
            </Box>
          ))}
      </Box>
      {pagination?.hasMore &&
        (hasLoadMoreButton ? (
          <Flex justifyContent="center" marginTop="6x">
            <Button
              variant="secondary"
              onPress={handleLoadMorePress}
              loading={pagination.isLoadingMore}
              testID="load-more-button"
              webProps={{ ref: loadMoreButtonRef }}
            >
              {t`Load more photos`}
            </Button>
          </Flex>
        ) : (
          <Box ref={sentinelRef} height="1px" aria-hidden="true" data-testid="scroll-sentinel" />
        ))}
    </>
  );

  if (!onReorder) {
    return grid;
  }

  return (
    <DndContext
      accessibility={accessibility}
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={sortableKeys} strategy={rectSortingStrategy}>
        {grid}
      </SortableContext>
      {/* The overlay follows the pointer so the tile can animate into its slot on release. */}
      <DragOverlay dropAnimation={dropAnimation} style={{ cursor: 'grabbing' }}>
        {draggingImage && (
          <GalleryTile
            image={draggingImage}
            index={draggingIndex}
            onImageClick={onImageClick}
            isDimmed={isDimmedImage(draggingImage, dimmedImageIds)}
            overlay={
              renderImageOverlay?.(draggingIndex) ??
              (isEditableGalleryImage(draggingImage) &&
              isDimmedImage(draggingImage, dimmedImageIds) ? (
                <GalleryPrivateBadge />
              ) : null)
            }
          />
        )}
      </DragOverlay>
    </DndContext>
  );
};

export default ImageGridContainer;
